// PrintVoucher.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from "@mui/material";
import { format } from "date-fns";
import { graphqlQuery } from "../../../../data/Axios/DynamicService";
import AGO_Logo from "../../../../Assets/Images/AGO-Logo.png";
import { AccountsandFinanceurl, companyManagementUrl } from "../../../../config";

const baseURL = process.env.REACT_APP_API_URL || 'https://matrix.mcb.co.ke/usermanagement/';

const baseURL2 = companyManagementUrl.uri;

const LOGO_ORIGIN = (() => {
  try {
    return new URL(baseURL2).origin;
  } catch {
    return 'https://matrix.mcb.co.ke';
  }
})();
 
// ---- Logo path normalization (identical logic to PrintInvoice.jsx, ----
// ---- except it resolves against LOGO_ORIGIN instead of baseURL)     ----
const normalizeLogoPath = (rawPath) => {
  if (!rawPath || typeof rawPath !== 'string') return null;
 
  const trimmed = rawPath.trim();
  if (!trimmed) return null;
 
  const isWindowsPath = /^[a-zA-Z]:[\\/]/.test(trimmed);
  if (isWindowsPath) return null;
 
  let cleaned = trimmed.replace(/^(\.\.\/|\.\/)+/, '');
  cleaned = cleaned.replace(/^\/+/, '');
 
  if (!cleaned.startsWith('assets/')) {
    return null;
  }
 
  return `${LOGO_ORIGIN.replace(/\/+$/, '')}/${cleaned}`;
};

// ---- Color extraction helpers ----
const DEFAULT_COLORS = { primary: "#8A3533", secondary: "#9EBB3C" };

const toHex = (r, g, b) =>
  `#${[r, g, b].map((v) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, "0")).join("")}`;

const colorDistance = (a, b) =>
  Math.sqrt((a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2);

const luminance = (hex) => {
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16) / 255;
  const g = parseInt(c.substring(2, 4), 16) / 255;
  const b = parseInt(c.substring(4, 6), 16) / 255;
  const [rl, gl, bl] = [r, g, b].map((v) =>
    v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
  );
  return 0.2126 * rl + 0.7152 * gl + 0.0722 * bl;
};

const readableTextColor = (hex) => (luminance(hex) > 0.5 ? "#1a1a1a" : "#ffffff");

const extractColorsFromImage = (imgSrc) => {
  return new Promise((resolve) => {
    if (!imgSrc) return resolve(DEFAULT_COLORS);

    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      try {
        const size = 60;
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, size, size);
        const { data } = ctx.getImageData(0, 0, size, size);

        const counts = {};
        for (let i = 0; i < data.length; i += 4) {
          const [r, g, b, a] = [data[i], data[i + 1], data[i + 2], data[i + 3]];
          if (a < 200) continue;
          const brightness = (r + g + b) / 3;
          if (brightness > 235 || brightness < 20) continue;

          const key = [
            Math.round(r / 16) * 16,
            Math.round(g / 16) * 16,
            Math.round(b / 16) * 16,
          ].join(",");
          counts[key] = (counts[key] || 0) + 1;
        }

        const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
        if (sorted.length === 0) return resolve(DEFAULT_COLORS);

        const primaryRgb = sorted[0][0].split(",").map(Number);
        const primary = toHex(...primaryRgb);

        let secondary = DEFAULT_COLORS.secondary;
        for (let i = 1; i < sorted.length; i++) {
          const rgb = sorted[i][0].split(",").map(Number);
          if (colorDistance(primaryRgb, rgb) > 90) {
            secondary = toHex(...rgb);
            break;
          }
        }

        resolve({ primary, secondary });
      } catch (err) {
        console.error("Color extraction failed:", err);
        resolve(DEFAULT_COLORS);
      }
    };
    img.onerror = () => resolve(DEFAULT_COLORS);
    img.src = imgSrc;
  });
};
// -----------------------------------

const PrintVoucher = ({ data }) => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const companyId = parseInt(decodedToken.CompanyId);
  const [lineItems, setLineItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);
  const [brandColors, setBrandColors] = useState(DEFAULT_COLORS);

  console.log("Voucher Data:", data);

  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const companyQuery = `
          {
          allCompanies {
                id
                companyName
                companyEmail
                contactEmail
                contactEmailPassword
                emailDisplayName
                emailClient
                emailClientPortNumber
                subscriptionId
                createdBy
                createdDate
                updatedBy
                updatedDate
                logoPath
                address
                poBox
                phone
                mobile
                website
                pinVat
                tagline
            }
        }
        `;

        const response = await graphqlQuery(
          companyQuery,
          companyManagementUrl.uri,
        );

        if (response && response.length > 0) {
          const userCompanyId = companyId;
          const company = response.find(c => c.id === userCompanyId) 
                        || response[0];
          setCompanyData(company);
          console.log("Company Data1:", response[0]);

          const normalizedLogoUrl = normalizeLogoPath(company.logoPath);
          setLogoUrl(normalizedLogoUrl);
          setLogoLoadFailed(false);

          if (normalizedLogoUrl) {
            const colors = await extractColorsFromImage(normalizedLogoUrl);
            setBrandColors(colors);
          } else {
            setBrandColors(DEFAULT_COLORS);
          }
        }
      } catch (err) {
        console.error("Error fetching company data:", err);
      }
    };

    fetchCompanyData();
  }, []);

  console.log("Company Data:", companyData);

  useEffect(() => {
    const fetchLineItems = async () => {
      if (!data || !data[0]?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const voucherId = data[0].id;

        const voucherLinesQuery = `
          query { 
            paymentVoucherLinesView(paymentVoucherLines: {paymentVoucherId: ${voucherId}}) {
              id
              paymentVoucherId
              voucherNumber
              lineNumber
              payeeTypeId
              payeeTypeName
              payeeId
              payeeName
              referenceId
              referenceNumber
              accountTypeId
              accountTypeName
              accountId
              accountName
              description
              amount
              withholdingTaxId
              withholdingTaxRate
              withholdingTaxAmount
              vatId
              vatAmount
              netLineAmount
              isAdvance
              isPosted
              allocationStatus
              allocatedAmount
              isActive
              createdBy
              createdDate
              updatedBy
              updatedDate
            } 
          }
        `;

        const response = await graphqlQuery(
          voucherLinesQuery, 
          AccountsandFinanceurl.uri,
        );

        console.log("Voucher Line Items Response:", response);

        if (response && response.paymentVoucherLinesView) {
          setLineItems(response.paymentVoucherLinesView);
        } else if (response && Array.isArray(response)) {
          setLineItems(response);
        } else {
          setLineItems([]);
        }
      } catch (err) {
        console.error("Error fetching voucher lines:", err);
        setError("Failed to load voucher items");
        setLineItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLineItems();
  }, [data]);

  const primaryText = readableTextColor(brandColors.primary);

  const printStyles = `
    @media print {
      body * {
        visibility: hidden;
      }
      .print-section, .print-section * {
        visibility: visible;
      }
      .print-section {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        background: white;
        padding: 0;
        margin: 0;
        transform: scale(0.95);
        transform-origin: top left;
      }
      .no-print {
        display: none !important;
      }
      .loading-section {
        display: none !important;
      }
      @page {
        margin: 10mm;
        size: A4 portrait;
      }
    }

    .print-container {
      min-height: 100vh;
      background: white;
      padding: 20px;
    }

    .print-section {
      padding: 25px;
      font-family: 'Georgia', 'Times New Roman', serif;
      background: white;
      max-width: 210mm;
      min-height: 297mm;
      margin: 0 auto;
      position: relative;
      border: 3px double ${brandColors.primary};
      box-shadow: 0 0 20px rgba(138, 53, 51, 0.1);
      font-size: 14px;
    }

    .loading-section {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 200px;
      text-align: center;
    }

    .error-section {
      background: #ffe6e6;
      border: 1px solid #ff4444;
      padding: 20px;
      border-radius: 5px;
      text-align: center;
      margin: 20px;
    }

    .voucher-header {
      text-align: center;
      margin-bottom: 30px;
      padding-bottom: 20px;
      border-bottom: 3px double ${brandColors.primary};
    }

    .voucher-logo-container {
      margin-bottom: 15px;
    }

    .voucher-logo {
      max-width: 180px;
      height: auto;
      max-height: 90px;
      object-fit: contain;
      display: block;
      margin: 0 auto;
    }

    .logo-placeholder {
      width: 180px;
      height: 90px;
      background: linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      border: 3px double ${brandColors.primary};
      color: ${primaryText};
      font-weight: bold;
      font-size: 18px;
      text-align: center;
      line-height: 1.2;
      margin: 0 auto;
    }

    .voucher-title {
      font-size: 28px;
      font-weight: bold;
      color: ${brandColors.primary};
      margin: 10px 0;
      text-transform: uppercase;
      letter-spacing: 2px;
      text-shadow: 1px 1px 2px rgba(0,0,0,0.1);
    }

    .voucher-subtitle {
      font-size: 16px;
      color: ${brandColors.secondary};
      font-style: italic;
      margin-bottom: 10px;
    }

    .firm-info {
      font-size: 12px;
      color: #333;
      line-height: 1.4;
      margin-bottom: 5px;
    }

    .voucher-details {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 20px;
      margin-bottom: 25px;
    }

    .payment-details-section {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 20px;
      margin-bottom: 25px;
    }

    .detail-box {
      padding: 15px;
      border: 1px solid #ddd;
      border-radius: 8px;
      background: #f9f9f9;
      color: #333;
    }

    .detail-title {
      font-weight: bold;
      color: ${brandColors.primary};
      margin-bottom: 10px;
      font-size: 14px;
    }

    .voucher-detail-group {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .voucher-detail-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 5px 0;
      border-bottom: 1px dotted #ccc;
    }

    .voucher-detail-label {
      font-weight: bold;
      color: ${brandColors.primary};
      min-width: 140px;
    }

    .voucher-detail-value {
      color: #333;
      font-weight: 500;
      text-align: right;
    }

    .payment-info {
      background: linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%);
      border: 2px solid ${brandColors.secondary};
      border-radius: 8px;
      padding: 15px;
      margin-bottom: 25px;
    }

    .payment-title {
      font-weight: bold;
      color: ${brandColors.primary};
      font-size: 16px;
      margin-bottom: 10px;
      text-align: center;
    }

    .payment-details {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
    }

    .voucher-table {
      width: 100%;
      border-collapse: collapse;
      border: 2px solid ${brandColors.primary};
      margin: 20px 0;
      font-size: 13px;
      background: white;
      color: #333;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }

    .voucher-table th {
      background: linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.primary} 100%);
      color: ${primaryText};
      font-weight: bold;
      padding: 12px 8px;
      border: 1px solid ${brandColors.primary};
      text-align: left;
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 1px;
    }

    .voucher-table td {
      padding: 10px 8px;
      border: 1px solid #ddd;
      vertical-align: top;
      background: white;
      color: #333;
    }

    .voucher-table tr:nth-child(even) td {
      background-color: #f9f9f9;
    }

    .amount-column {
      text-align: right;
      font-family: 'Courier New', monospace;
      font-weight: 600;
      color: #333;
    }

    .voucher-summary {
      width: 350px;
      margin-left: auto;
      margin-top: 25px;
      border: 2px solid ${brandColors.primary};
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 3px 6px rgba(0,0,0,0.1);
    }

    .summary-table {
      width: 100%;
      border-collapse: collapse;
    }

    .summary-table td {
      padding: 12px 15px;
      border-bottom: 1px solid #ddd;
    }

    .summary-label {
      font-weight: bold;
      background: linear-gradient(135deg, #f9f9f9 0%, #e9e9e9 100%);
      color: ${brandColors.primary};
      font-size: 13px;
    }

    .summary-value {
      text-align: right;
      font-family: 'Courier New', monospace;
      font-weight: bold;
      color: #333;
      font-size: 13px;
    }

    .voucher-total {
      background: linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.primary} 100%);
      color: ${primaryText} !important;
      font-size: 16px;
    }

    .voucher-total .summary-value {
      color: ${primaryText} !important;
      font-weight: 800;
      font-size: 16px;
    }

    .voucher-footer {
      margin-top: 40px;
      padding-top: 25px;
      border-top: 3px double ${brandColors.secondary};
      text-align: center;
      font-size: 11px;
      color: #666;
    }

    .thank-you {
      font-size: 16px;
      font-weight: bold;
      color: ${brandColors.primary};
      margin-bottom: 15px;
      font-style: italic;
    }

    .signature-area {
      margin-top: 40px;
      text-align: center;
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 40px;
      max-width: 500px;
      margin-left: auto;
      margin-right: auto;
    }

    .signature-line {
      border-top: 2px solid ${brandColors.primary};
      width: 100%;
      margin: 0 auto;
      padding-top: 8px;
      font-size: 12px;
      color: ${brandColors.primary};
      font-weight: 600;
      text-align: center;
    }

    .stamp-area {
      position: absolute;
      bottom: 100px;
      right: 50px;
      opacity: 0.8;
    }

    .stamp {
      border: 2px solid ${brandColors.primary};
      border-radius: 5px;
      padding: 10px;
      transform: rotate(5deg);
      background: white;
      font-size: 10px;
      font-weight: bold;
      color: ${brandColors.primary};
      text-align: center;
    }

    .print-controls {
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 10000;
      background: white;
      padding: 20px;
      border-radius: 10px;
      box-shadow: 0 6px 20px rgba(0,0,0,0.3);
      border: 3px solid ${brandColors.primary};
      display: flex;
      gap: 15px;
    }

    .print-btn {
      background: linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.primary} 100%);
      color: ${primaryText};
      border: none;
      padding: 12px 25px;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      font-weight: bold;
      transition: all 0.3s ease;
      box-shadow: 0 3px 8px rgba(0,0,0,0.2);
    }

    .print-btn:hover {
      transform: translateY(-2px);
      opacity: 0.88;
      box-shadow: 0 5px 12px rgba(0,0,0,0.3);
    }

    .close-btn {
      background: linear-gradient(135deg, #666 0%, #555 100%);
      color: white;
      border: none;
      padding: 12px 25px;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      font-weight: bold;
      transition: all 0.3s ease;
      box-shadow: 0 3px 8px rgba(0,0,0,0.2);
    }

    .close-btn:hover {
      background: linear-gradient(135deg, #555 0%, #666 100%);
      transform: translateY(-2px);
      box-shadow: 0 5px 12px rgba(0,0,0,0.3);
    }
  `;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate() - 1).padStart(2, '0');

      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  const handleImageError = (e) => {
    setLogoLoadFailed(true);
    const img = e.target;
    img.src = AGO_Logo;
  };

  const voucher = data?.[0] || {};

  const subtotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  const withholdingTaxTotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.withholdingTaxAmount) || 0), 0);
  const vatTotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.vatAmount) || 0), 0);
  const netAmount = subtotal - withholdingTaxTotal + vatTotal;

  const companyInfo = {
    name: companyData?.companyName || "set at company management",
    address: companyData?.address || "set at company management",
    poBox: companyData?.poBox || "set at company management",
    phone: companyData?.phone || "set at company management",
    mobile: companyData?.mobile || "set at company management",
    email: companyData?.companyEmail || "set at company management",
    website: companyData?.website || "set at company management",
    pinVat: companyData?.pinVat || "set at company management",
    tagline: companyData?.tagline || "set at company management"
  };

  const handlePrint = () => {
    window.print();
  };

  const handleClose = () => {
    window.close();
  };

  if (loading) {
    return (
      <Box className="print-container">
        <style>{printStyles}</style>
        <Box className="loading-section">
          <CircularProgress size={40} style={{ marginBottom: 20 }} />
          <Typography variant="h6">Loading Voucher Details...</Typography>
          <Typography variant="body2" style={{ marginTop: 10 }}>
            Fetching voucher items for Voucher #{voucher.id}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="print-container">
        <style>{printStyles}</style>
        <Box className="error-section">
          <Typography variant="h6" color="error">Error Loading Voucher</Typography>
          <Typography variant="body2" style={{ marginTop: 10 }}>
            {error}
          </Typography>
          <button 
            className="print-btn" 
            onClick={() => window.location.reload()}
            style={{ marginTop: 15 }}
          >
            Retry
          </button>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="print-container">
      <style>{printStyles}</style>

      <Box className="print-controls no-print">
        <button 
          className="print-btn" 
          onClick={handlePrint}
        >
          🖨️ Print
        </button>
        <button 
          className="close-btn" 
          onClick={handleClose}
        >
          ✖ Close
        </button>
      </Box>

      <Box className="print-section">
        <div className="voucher-header">
          <div className="voucher-logo-container">
            <img 
              src={logoUrl && !logoLoadFailed ? logoUrl : AGO_Logo}
              alt={companyInfo.name}
              className="voucher-logo"
              onError={handleImageError}
            />
          </div>

          <div className="voucher-title">PAYMENT VOUCHER</div>
          <div className="voucher-subtitle">{companyInfo.tagline}</div>

          <div className="firm-info">
            <div>{companyInfo.name}</div>
            <div>{companyInfo.address}</div>
            <div>{companyInfo.poBox}</div>
            <div>Tel: {companyInfo.phone} | Email: {companyInfo.email}</div>
            <div>PIN/VAT: {companyInfo.pinVat}</div>
          </div>
        </div>

        <div className="payment-details-section">
          <div className="detail-box">
            <div className="detail-title">PAYMENT DETAILS</div>
            <div className="voucher-detail-item">
              <span className="voucher-detail-label">Voucher Date:</span>
              <span className="voucher-detail-value">{formatDate(voucher.voucherDate)}</span>
            </div>
            <div className="voucher-detail-item">
              <span className="voucher-detail-label">Payment Date:</span>
              <span className="voucher-detail-value">{formatDate(voucher.paymentDate) || ''}</span>
            </div>
            <div className="voucher-detail-item">
              <span className="voucher-detail-label">Payment Mode:</span>
              <span className="voucher-detail-value">{voucher.paymentModeName}</span>
            </div>
          </div>

          <div className="detail-box">
            <div className="detail-title">ACCOUNT DETAILS</div>
            <div className="voucher-detail-item">
              <span className="voucher-detail-label">Payment Account:</span>
              <span className="voucher-detail-value">{voucher.paymentAccountName}</span>
            </div>
            <div className="voucher-detail-item">
              <span className="voucher-detail-label">Account Type:</span>
              <span className="voucher-detail-value">{voucher.paymentAccountTypeName}</span>
            </div>
            <div className="voucher-detail-item">
              <span className="voucher-detail-label">Reference:</span>
              <span className="voucher-detail-value">{voucher.referenceNumber || ''}</span>
            </div>
          </div>
        </div>

        <div className="voucher-details">
          <div className="voucher-detail-group">
            <div className="voucher-detail-item">
              <span className="voucher-detail-label">Description:</span>
              <span className="voucher-detail-value">{voucher.description}</span>
            </div>
          </div>

          <div className="voucher-detail-group">
            <div className="voucher-detail-item">
              <span className="voucher-detail-label">Status:</span>
              <span className="voucher-detail-value" style={{ 
                color: voucher.isPosted === 1 ? '#4CAF50' : '#FF9800',
                fontWeight: 'bold'
              }}>
                {voucher.isPosted === 1 ? 'POSTED' : 'PENDING'}
              </span>
            </div>
          </div>
        </div>

        <div className="payment-info">
          <div className="payment-title">PAYMENT BREAKDOWN</div>
          <div className="payment-details">
            <div className="voucher-detail-item">
              <span className="voucher-detail-label">Total Amount:</span>
              <span className="voucher-detail-value" style={{fontWeight: 'bold'}}>
                {formatCurrency(voucher.totalAmount)}
              </span>
            </div>
            <div className="voucher-detail-item">
              <span className="voucher-detail-label">Currency:</span>
              <span className="voucher-detail-value">{voucher.currencyCode || 'KES'}</span>
            </div>
          </div>
        </div>

        <TableContainer component={Paper} elevation={0}>
          <Table className="voucher-table">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: '40px' }}>#</TableCell>
                <TableCell>Payee / Description</TableCell>
                <TableCell style={{ width: '120px', textAlign: 'right' }}>Amount</TableCell>
                <TableCell style={{ width: '80px', textAlign: 'right' }}>W/Tax Rate</TableCell>
                <TableCell style={{ width: '100px', textAlign: 'right' }}>W/Tax Amount</TableCell>
                <TableCell style={{ width: '100px', textAlign: 'right' }}>VAT Amount</TableCell>
                <TableCell style={{ width: '120px', textAlign: 'right' }}>Net Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lineItems.length > 0 ? (
                lineItems.map((item, index) => (
                  <TableRow key={item.id || index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <div style={{ fontWeight: 'bold' }}>{item.payeeName || 'N/A'}</div>
                      <div>{item.description || 'Payment description'}</div>
                      {item.accountName && (
                        <div style={{ fontSize: '11px', color: '#666', fontStyle: 'italic' }}>
                          Account: {item.accountName}
                        </div>
                      )}
                      {item.referenceNumber && (
                        <div style={{ fontSize: '11px', color: '#666' }}>
                          Ref: {item.referenceNumber}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="amount-column">
                      {formatCurrency(item.amount)}
                    </TableCell>
                    <TableCell className="amount-column">
                      {item.withholdingTaxRate ? `${item.withholdingTaxRate}%` : ''}
                    </TableCell>
                    <TableCell className="amount-column">
                      {formatCurrency(item.withholdingTaxAmount)}
                    </TableCell>
                    <TableCell className="amount-column">
                      {formatCurrency(item.vatAmount)}
                    </TableCell>
                    <TableCell className="amount-column" style={{fontWeight: 'bold'}}>
                      {formatCurrency(item.netLineAmount)}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} style={{ textAlign: 'center', color: '#666', fontStyle: 'italic' }}>
                    No voucher line items recorded
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <div className="voucher-summary">
          <table className="summary-table">
            <tbody>
              <tr>
                <td className="summary-label">Gross Amount:</td>
                <td className="summary-value">{formatCurrency(subtotal)}</td>
              </tr>
              <tr>
                <td className="summary-label">Withholding Tax:</td>
                <td className="summary-value">{formatCurrency(withholdingTaxTotal)}</td>
              </tr>
              <tr>
                <td className="summary-label">VAT Amount:</td>
                <td className="summary-value">{formatCurrency(vatTotal)}</td>
              </tr>
              <tr className="voucher-total">
                <td className="summary-label">NET PAYMENT:</td>
                <td className="summary-value">{formatCurrency(netAmount)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {voucher.notes && (
          <div style={{ marginTop: '20px', padding: '15px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: '5px' }}>
            <div style={{ fontWeight: 'bold', color: '${brandColors.primary}', marginBottom: '8px' }}>Notes:</div>
            <div style={{ fontSize: '12px', color: '#333' }}>{voucher.notes}</div>
          </div>
        )}

        <div className="voucher-footer">
          <div className="thank-you">Payment Authorization</div>

          <div style={{ fontSize: '12px', color: '#666', marginBottom: '20px' }}>
            This payment voucher authorizes the disbursement of funds as detailed above.
          </div>

          <div className="signature-area">
            <div>
              <div className="signature-line">
                Prepared By
              </div>
            </div>
            <div>
              <div className="signature-line">
                Authorized Signatory
              </div>
            </div>
          </div>

          <div className="stamp-area">
            <div className="stamp">
              PAID<br/>
              {formatDate(voucher.paymentDate) || formatDate(voucher.voucherDate)}
            </div>
          </div>

          <div style={{ marginTop: '25px', fontSize: '10px', color: '#999', fontStyle: 'italic' }}>
            Computer generated voucher • Valid without signature • Printed on: {format(new Date(), 'dd/MM/yyyy HH:mm')}
          </div>
        </div>
      </Box>
    </Box>
  );
};

export default PrintVoucher;
